import { useState, useEffect } from 'react';
import { DashboardLayout } from '~/Layouts';
import {
    Container,
    IntroSteps,
    PageHeader,
    PageBanner,
    Tabs,
} from '~/Common';
import {
    Related
} from '~/Deals'
import {
    List
} from '~/Investments';
import {
    Flex,
    Heading,
    Text,
    Button,
    NavLink as ThemedNavLink,
} from '@theme-ui/components';
import { Icon } from 'assets/Icon'
import { useDispatch, connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getPortfolioStatsAsync, setPortfolioStatus } from 'features/portfolio/portfolioSlice';
import { getWatchlistAsync, getWatchlistDealsAsync } from 'features/watchlist/watchlistSlice';
import { getMarketDataAsync } from "features/theme/themeSlice";
import { APP_FEATURES } from 'app/constants';
import { VerifySteps } from '~/Common/VerifySteps';
import { Carousel } from "~/MarketData";
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { NavLink } from 'react-router-dom';
import { AsxAnnouncementsCompact } from '~/ContentBlocks/AsxAnnouncements';
import PortfolioSummary from '~/Portfolio/PortfolioSummary';
import { setProfileByUUID } from 'features/auth/authSlice';
import { useResponsiveValue } from '@theme-ui/match-media';

const PortfolioDashboard = ({ user, portfolio, watchlist, theme }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    useDocumentTitle(t('Portfolio Dashboard'));
    const [ selectedTab, setSelectedTab ] = useState('listed');
    const [ selectedAnnouncementTab, setSelectedAnnouncementTab ] = useState('following');
    const activeProfile = useSelector((state) => state.user?.activeProfile);
    const isMobile = useResponsiveValue([true, true, true, false]);
    

    const allAnnouncementsData = watchlist?.watchlist?.all_announcements ? {
        title: 'Recent Announcement Summaries',
        announcements: watchlist?.watchlist?.all_announcements.map((announcement) => ({
            announcement: announcement?.url,
            label: announcement?.title,
            priceSensitive: announcement?.price_sensitive,
            summary: announcement?.summary,
            releaseDate: announcement?.released_at,
            company: {
                name: announcement?.company?.name,
                code: announcement?.company?.code,
                logo: announcement?.company?.logo,
                slug: announcement?.company?.slug
            }
        }))
    } : null;

    const followingAnnouncementsData = watchlist?.watchlist?.user_announcements ? {
        title: 'Recent Announcement Summaries',
        announcements: watchlist?.watchlist?.user_announcements.map((announcement) => ({
            announcement: announcement?.url,
            label: announcement?.title,
            priceSensitive: announcement?.price_sensitive,
            summary: announcement?.summary,
            releaseDate: announcement?.released_at,
            company: {
                name: announcement?.company?.name,
                code: announcement?.company?.code,
                logo: announcement?.company?.logo,
                slug: announcement?.company?.slug
            }
        }))
    } : null;

    const tabs = [
        {
            key: 'listed',
            name: 'Listed Shareholdings',
            icon: 'clipboard-left',
            onClick: () => setSelectedTab('listed')
        },
        {
            key: 'unlisted',
            name: 'Unlisted Shareholdings',
            icon: 'clipboard-right',
            onClick: () => setSelectedTab('unlisted')
        },
    ];

    const announcementTabs = [
        {
            key: 'following',
            name: 'Following',
            icon: 'watchlist',
            onClick: () => setSelectedAnnouncementTab('following')
        },
        {
            key: 'all',
            name: 'Companies on Liquidity',
            icon: 'bell',
            onClick: () => setSelectedAnnouncementTab('all')
        },
    ];

    const noFollowingAnnouncementsLink = (
        <Flex sx={{
            pt: 4,
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            mx: 'auto',
            textAlign: 'center'
        }}>
            <Icon icon="watchlist-thin" color="tealDark" mb={3} width="48px" />
            <Heading variant="h2" sx={{ fontSize: '36px' }}>{t('Start following companies')}</Heading>
            <Text mt={1} variant="subtext">{t('Start following the companies that interest you to receive real time announcement notifications')}</Text>
            <Button
                as={NavLink}
                to={'/companies'}
                mt={4}
                sx={{
                    minWidth: '160px',
                    fontSize: '18px',
                }}
            >
                Follow A Company
            </Button>
        </Flex>
    );

    useEffect(() => {
        if (portfolio.stats.status === 'idle') {
            dispatch(getPortfolioStatsAsync());
        }
        if(watchlist.deals.status === 'idle'){
            dispatch(getWatchlistDealsAsync());
        }
        if(watchlist.status === 'attempted' && (watchlist.watchlist?.all_announcements == null || watchlist.watchlist?.user_announcements == null)){
            dispatch(getWatchlistAsync());
        }
    }, [dispatch, watchlist, portfolio]);

    useEffect(() => {
        if (theme.status === 'idle') {
            dispatch(getMarketDataAsync());
        }
    }, [theme, dispatch]);

    const profileTabNames = {
        individual: 'Individual',
        company: 'Company',
        superannuation: 'Super Fund',
        superannuation_company: 'Super Company',
        trust: 'Trust',
        trust_company: 'Trust Company',
    }

    const handleProfileTabChange = (uuid) => {
        dispatch(setProfileByUUID(uuid));
        dispatch(setPortfolioStatus('idle'));
    };

    return (
        <DashboardLayout dark={true} p={0}>
            <PageBanner variant="layouts.portfolio" img="industries" heightOverride={isMobile ? 'auto' : '700px'}>
                <Heading mt={3} as="h2" variant="industry_page_title" mb={isMobile ? '48px' : 0}>{t('My Portfolio')}</Heading>
                {!isMobile && (
                    <Flex as="nav" my={4} mx="auto" sx={{
                        backgroundColor: 'tealLighter',
                        border: '1px solid #D2FBF7',
                        borderRadius: '9999px',
                        height: 'fit-content',
                        width: 'fit-content',
                    }}>
                        {user.profiles.map((profile, index) => (
                            <ThemedNavLink 
                                sx={{
                                    height: '32px',
                                    backgroundColor: activeProfile?.uuid === profile?.uuid ? 'white' : 'tealLighter',
                                    borderRadius: '9999px',
                                    margin: '2px',
                                    cursor: 'pointer',
                                    color: 'tealDark',
                                    fontSize: '18px',
                                }}
                                key={index}
                                onClick={() => handleProfileTabChange(profile.uuid)}
                            >
                                {profileTabNames[profile.type]}
                            </ThemedNavLink>
                        ))}
                    </Flex>
                )}

                <PortfolioSummary activeProfile={activeProfile} portfolio={portfolio} user={user} />
            </PageBanner>

            <Container variant="layouts.default" px={0} mt={'-60px'} sx={{ position: 'relative', zIndex: 1, minWidth: '100%' }}>
                <PageHeader />
                <VerifySteps />
                <Carousel />
            </Container>


            <Container variant="layouts.default" sx={{ position: 'relative', zIndex: 1 }}>

                <IntroSteps />

                <Tabs variant='announcements' sx={{ mb: '0px' }} tabs={announcementTabs} selected={selectedAnnouncementTab} />
                {selectedAnnouncementTab === 'following' ? (
                    <AsxAnnouncementsCompact sx={{py: 0}} user={user} data={followingAnnouncementsData} emptyAction={noFollowingAnnouncementsLink} isLoading={watchlist?.status === 'loading'} />
                ) : (
                    <AsxAnnouncementsCompact sx={{py: 0}} user={user} data={allAnnouncementsData} isLoading={watchlist?.status === 'loading'} />
                )}

                <Tabs variant='announcements' sx={{ mt: '64px' }} tabs={tabs} selected={selectedTab} />
                <List portfolio={portfolio} type={selectedTab} />

                {!!APP_FEATURES.following && watchlist?.deals?.live?.results.length > 0 && (
                    <Related heading="Following" showDiscoverMore={true} to="/following" deals={watchlist?.deals?.live?.results} sx={{ mt: 5, mb: 4 }} />
                )}
            </Container>
        </DashboardLayout>
    );
};

const mapStateToProps = (state) => {
    return { user: state.user?.user, portfolio: state.portfolio, watchlist: state.watchlist, settings: state.theme?.settings, theme: state.theme }
}

export default connect(mapStateToProps, null)(PortfolioDashboard);
